import React from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import TestimonialVideoBlock from './TestimonialVideoBlock';
import RecentReviewsBlock from './RecentReviewsBlock';
import SocialLinksRow from './SocialLinksRow';
import PartnerLogosStrip from './PartnerLogosStrip';
import FadeIn from '../../../components/animations/FadeIn';
import { useGoogleStats } from '../../../hooks/useGoogleStats';

// Last-known Google aggregate. Shown for the brief moment before
// useGoogleStats resolves (and as a safety net if the Supabase fetch
// fails). Matches the value RecentInstallsSection uses so the page
// never flashes a worse number from one section to another. Live
// values from `useGoogleStats` replace this the moment the fetch lands;
// the hook's module-level cache means one fetch per page load shared
// across this section, RecentInstallsSection, and GoogleReviewsBadge.
const FALLBACK_TOTAL_REVIEWS = 32;

// "Credibility ladder" section, restructured 2026-05-21. Section background
// stays white — dark-glass tiles pop against it. Five tiers from top to
// bottom: heading → A numbers row → B featured video → C reviews grid →
// D partner logos (unchanged) → E service-area pill. SocialLinksRow is
// kept in its prior relative slot between C and D.

// Tier A — single proof stat. Cyan-gradient number + caps label.
// Matches .bridge-stat-num vocabulary but scaled down ~30%.
const ProofStat = ({ value, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    className="text-center"
  >
    <div
      className="font-black leading-none mb-2"
      style={{
        fontSize: 'clamp(40px, 5vw, 56px)',
        background: 'linear-gradient(180deg, var(--blue-glow) 0%, #07B0E9 50%, var(--blue-deep) 100%)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent',
        letterSpacing: '-0.03em',
        filter: 'drop-shadow(0 4px 14px rgba(7, 176, 233, 0.35))',
      }}
    >
      {value}
    </div>
    <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-gray-500">
      {label}
    </div>
  </motion.div>
);

// Tier E — small dark-glass pill reinforcing the service-area narrative.
const ServiceAreaPill = () => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.4 }}
    transition={{ duration: 0.5, ease: 'easeOut' }}
    className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-sm"
    style={{
      background: 'rgba(10, 22, 40, 0.85)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(7, 176, 233, 0.4)',
      boxShadow: '0 8px 28px rgba(7, 176, 233, 0.25)',
      color: 'rgba(255, 255, 255, 0.92)',
    }}
  >
    <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} aria-hidden="true" />
    <span
      className="w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse"
      style={{ background: '#07B0E9' }}
      aria-hidden="true"
    />
    <span>
      Currently serving <strong className="font-bold">Dayton &amp; Cincinnati</strong>
      <span className="mx-2 text-white/40">·</span>
      <strong className="font-bold">13 cities</strong> by end of 2026
    </span>
  </motion.div>
);

const SocialProofSection = () => {
  // Pulled from the same Supabase aggregate that powers the
  // GoogleReviewsBadge widget and the RecentInstallsSection summary.
  // Shared cache → no extra network round-trip.
  const { stats } = useGoogleStats();
  const totalReviews = stats?.totalReviews ?? FALLBACK_TOTAL_REVIEWS;
  const formattedTotal = Math.floor(totalReviews).toLocaleString('en-US');

  return (
  <section className="py-12 lg:py-16 bg-white">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
      {/* Heading */}
      <FadeIn className="text-center mb-10 lg:mb-12">
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
          style={{
            backgroundColor: 'rgba(7,176,233,0.10)',
            color: '#07B0E9',
            border: '1px solid rgba(7,176,233,0.25)',
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
          Real Homeowners. Real Results.
        </div>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3"
          style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
        >
          See why homeowners choose OpenbooQ
        </h2>
        <p className="text-gray-500 text-base lg:text-lg max-w-2xl mx-auto">
          Hear from a customer who replaced their roof with us, and read recent Google reviews from homeowners — <strong className="font-semibold text-gray-700">starting in Dayton &amp; Cincinnati, expanding to 13 cities by end of 2026.</strong>
        </p>
      </FadeIn>

      {/* Tier A — numbers row. First stat is LIVE from useGoogleStats so
          the verified-review count stays current as new Google reviews
          land in Supabase. The hook's module-level cache means this
          section, TrustBridge, RecentInstallsSection, and
          GoogleReviewsBadge all share a single network fetch per page
          load.
          2026-05-24 cleanup: the "0 Complaints" stat was removed
          entirely (desktop AND mobile). Grid simplified to two columns
          on every viewport — was grid-cols-1 sm:grid-cols-3 (mobile
          stacked, desktop 3-across). Now grid-cols-2 puts the two
          remaining stats side-by-side on mobile (more compact, less
          vertical space) and keeps them paired on desktop. Labels can
          wrap under the number on narrow viewports if they don't fit
          single-line — number size is held by the clamp() in ProofStat
          and not shrunk. */}
      <div className="grid grid-cols-2 gap-6 sm:gap-10 lg:gap-16 max-w-3xl mx-auto mb-12 lg:mb-16">
        <ProofStat value={formattedTotal} label="Verified Google reviews" delay={0.08} />
        <ProofStat value="100%" label="Five-star ratings" delay={0.16} />
      </div>

      {/* Tier B — featured customer video (dark-glass wrapper styling lives in TestimonialVideoBlock) */}
      <FadeIn className="max-w-[760px] mx-auto mb-12 lg:mb-16" delay={0.08}>
        <TestimonialVideoBlock />
      </FadeIn>

      {/* Tier C — reviews grid (RecentReviewsBlock now renders a 3×2
          glassmorphism grid internally, with a featured card spanning
          col-span-2 in row 1).
          Bottom margin bumped 2026-05-23 from mb-12 lg:mb-16 (48-64px)
          to mb-24 lg:mb-32 (96-128px) — the previous spacing crowded the
          social links row directly under the cards. The backdrop stage
          inside the reviews block also extends ~64px below the grid via
          its -inset-y padding; the extra section margin gives clear
          breathing room between the stage's faded bottom edge and the
          Facebook/Instagram/LinkedIn/TikTok icons. */}
      <div className="mb-24 lg:mb-32">
        <RecentReviewsBlock />
      </div>

      {/* Social links — kept in prior relative slot between reviews and partners */}
      <FadeIn delay={0.16}>
        <SocialLinksRow />
      </FadeIn>

      {/* Tier D — partner logos (unchanged) */}
      <FadeIn delay={0.24}>
        <PartnerLogosStrip />
      </FadeIn>

      {/* Tier E — service-area pill */}
      <div className="flex justify-center mt-10 lg:mt-12">
        <ServiceAreaPill />
      </div>
    </div>
  </section>
  );
};

export default SocialProofSection;
